import dayjs from "dayjs";
import { Airport, FareDetails, PassengerTypes, TripDetails } from "redmond";
import { CrossSellPrebookingInput } from "../actions/actions";

/**
 * Derives the hotel cross-sell search inputs from the selected trip, so the
 * availability fetch can start at confirm-press instead of waiting for the
 * booking to finalize (~30s of fulfillment polling). Returns undefined when the
 * trip has no outbound slice to search against.
 */
export const buildCrossSellPrebookingInput = (
  tripDetails: TripDetails,
  airportMap: { [key: string]: Airport }
): CrossSellPrebookingInput | undefined => {
  const outbound = tripDetails.slices[0];
  if (outbound == null) return undefined;

  // Match getReturnSlice (redmond): only a 2-slice trip has a return leg. 3+ slices
  // (multi-city) are treated as one-way, same as the bookedItinerary/finalize path.
  const inbound =
    tripDetails.slices.length === 2 ? tripDetails.slices[1] : undefined;
  const outboundLastSegment =
    outbound.segmentDetails[outbound.segmentDetails.length - 1];

  // Hotel check-in follows the outbound arrival; check-out follows the return
  // arrival (or a 1-night stay for one-way trips).
  const departureDate = new Date(
    outboundLastSegment?.zonedArrivalTime ?? outbound.arrivalTime
  );
  const returnDate =
    inbound != null
      ? new Date(
          inbound.segmentDetails[0]?.zonedArrivalTime ?? inbound.arrivalTime
        )
      : dayjs(departureDate).add(1, "day").toDate();

  const destinationCode = outbound.destinationCode;

  const paxPricings =
    (tripDetails.fareDetails[0] as FareDetails)?.paxPricings ?? [];
  // Match the bookedItinerary path (passengers.alone + withLapInfants): count all
  // seated travelers — adults, children, and seated infants — i.e. everyone except
  // lap infants.
  const adultPaxCount =
    paxPricings
      .filter((pax) => pax.paxType !== PassengerTypes.InfantInLap)
      .reduce((sum, pax) => sum + pax.quantity, 0) || 1;

  return {
    destinationCode,
    destination:
      airportMap[destinationCode]?.cityName ||
      outbound.destinationName ||
      destinationCode,
    departureDate,
    returnDate,
    adultPaxCount,
    hasReturn: inbound != null,
  };
};
